import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { mixins } from "vue-class-component";
import Workspaces from "@/store/modules/Workspaces";
import Factors from "@/store/modules/Factors";
import Choices from "@/store/modules/Choices";
import Viewpoints from "@/store/modules/Viewpoints";
import Decisions from "@/store/modules/Decisions";
import ViewWrapper from "@/components/ui/ViewWrapper.vue";
import TabManagment from "@/components/mixins/TabManagment";
import CompareItems from "@/components/mixins/CompareItems";
import {
    Pagination,
    Tab,
    Locks,
    UiSettings,
    ScoreOptions,
    ScoreClassSettings,
    FactorOptions,
    Factor,
    Choice,
    Viewpoint,
} from "@/graphql/API";
import CompareObjects from "@/components/compare/CompareObjects.vue";
import FactorNav from "@/components/model/FactorNav.vue";
import ViewAppCreator from "@/components/apps/ViewAppCreator.vue";
import CompareSettings from "@/components/compare/CompareSettings.vue";
import CompareOptions from "@/components/compare/CompareOptions.vue";
import { saveTab } from "@/helpers/TabHelper";

const choiceModule = getModule(Choices);
const viewpointsModule = getModule(Viewpoints);
const modelModule = getModule(Factors);
const decisionsModule = getModule(Decisions);
const workspaceModule = getModule(Workspaces);

@Component({
    components: {
        ViewWrapper,
        CompareObjects,
        FactorNav,
        ViewAppCreator,
        CompareSettings,
        CompareOptions,
    },
})
export default class CompareBase extends mixins(CompareItems) {
    @Prop({ default: "compare", type: String })
    type!: string;

    sidebarIndex = "sidebar-close";
    settingsMode = "tab-settings";
    mainColumnWidth = 400;
    dataColumnWidth = 300;
    showHeader = true;
    selectedFactorLoading = false;

    loadedDataItemId: number | null = null;
    factorSelectOpen = false;
    chosenFactor: number[] = [];

    unSavedWarningOpen = false;
    saving = false;

    // Compare Tab Settings
    dataItem: Factor | Choice | Viewpoint | null = null;
    columns: "viewpoints" | "factors" | "choices" = "viewpoints";
    rows: "viewpoints" | "factors" | "choices" = "factors";
    data: "viewpoints" | "factors" | "choices" = "choices";

    pagination: Pagination = {
        columns: {
            all: false,
            items: 20,
            position: 0,
        },
    };

    indexIds: { [id: string]: "factor" | "choice" | "viewpoint" } = {
        factors: "factor",
        choices: "choice",
        viewpoints: "viewpoint",
    };
    private indexConversion: {
        [index: string]: "factors" | "choices" | "viewpoints";
    } = {
        factor: "factors",
        choice: "choices",
        viewpoint: "viewpoints",
    };

    indexes = {
        rows: "choices",
        columns: "viewpoints",
        data: "factors",
    };

    allFactors: Factor[] = [];
    rootFactorId = -1;

    appCreateOpen = false;

    get deactiveFactors(): number[] {
        return [...this.hiddenFactors, ...this.unselectedFactors];
    }

    get axisIndex(): {
        rows: "viewpoints" | "factors" | "choices";
        columns: "viewpoints" | "factors" | "choices";
        data: "viewpoints" | "factors" | "choices";
    } {
        return {
            rows: this.rows,
            columns: this.columns,
            data: this.data,
        };
    }

    /* Tab Settings */
    get currentUiOptions(): string {
        return JSON.stringify({
            mainColumn: this.mainColumnWidth,
            dataColumn: this.dataColumnWidth,
            paddingX: this.paddingX,
            paddingY: this.paddingY,
            borderX: this.borderX,
            borderY: this.borderY,
            pagination: this.pagination,
            rootFactor: this.rootFactorId,
            fullSingleCol: this.fullSingleCol,
        });
    }

    get currentEditFlags(): string {
        return JSON.stringify({
            choice: this.choiceEditable,
            score: this.scoresEditable,
            customScore: this.customScore,
            classRating: this.classRating,
            weights: this.weightsEditable,
            scoreRule: this.scoreRuleEditable,
            locks: this.locks,
            allChoices: this.allChoicesActive,
            allViewpoints: this.allViewpointsActive,
            allFactors: this.activeFactors.all,
            userChoices: this.userChoicesEnabled,
            userViewpoints: this.userViewpointsEnabled,
            selectUserChoices: this.selectUserChoices,
            selectUserViewpoints: this.selectUserViewpoints,
        });
    }

    get currentDisplayFlags(): string {
        return JSON.stringify({
            score: this.scoreDisplay,
            classSelector: this.classSelector,
            factor: this.factorDisplay,
        });
    }
    /* End of Tab Settings */

    /* URL query props */
    get workspaceId(): number | null {
        return workspaceModule.selectedWorkspaceId;
    }

    get urlSettings(): { [id: string]: string | number } {
        if (this.workspaceId && this.decisionId) {
            return {
                w: this.workspaceId,
                f: this.decisionId,
                settings: "true",
                rows: this.rows,
                columns: this.columns,
                data: this.data,
                dataId: this.dataItem ? `${this.dataItem.id}` : "null",
                rootFactor: this.rootFactorId,
                // choices: this.selectedChoices.join("-"),
                // viewpoints: this.selectedViewpoints.join("-"),
            };
        } else {
            return {};
        }
    }

    get factorsParams(): string {
        if (this.deactiveFactors.length) {
            return "factors=" + this.deactiveFactors.join("&factors=");
        } else {
            return "factors=";
        }
    }

    get choicesParams(): string {
        if (this.selectedChoices.length) {
            return "choices=" + this.selectedChoices.join("&choices=");
        } else {
            return "choices=";
        }
    }

    get viewpointsParams(): string {
        if (this.selectedViewpoints.length) {
            return "viewpoints=" + this.selectedViewpoints.join("&viewpoints=");
        } else {
            return "viewpoints=";
        }
    }

    get selectedObjectParams(): string {
        return `&${this.choicesParams}&${this.viewpointsParams}&${this.factorsParams}`;
    }

    get queryParams(): string {
        return Object.keys(this.urlSettings)
            .map((key: string) => key + "=" + this.urlSettings[key])
            .join("&");
    }

    get shareLink(): string {
        return `${window.location.origin}${window.location.pathname}?${this.queryParams}${this.selectedObjectParams}`;
    }

    /* End of URL query props */

    get factorsChanged(): boolean {
        if (this.tabFactorIds.length == this.deactiveFactors.length) {
            if (
                this.deactiveFactors.every((val: number) =>
                    this.tabFactorIds.includes(val)
                ) &&
                this.tabFactorIds.every((val: number) =>
                    this.deactiveFactors.includes(val)
                )
            ) {
                return false;
            } else {
                return true;
            }
        } else {
            return true;
        }
    }

    private getAxisParam(
        id: string
    ): "factors" | "choices" | "viewpoints" | null {
        if (this.routeQuery[id]) {
            if (this.routeQuery[id] == "factors") {
                return "factors";
            } else if (this.routeQuery[id] == "choices") {
                return "choices";
            } else {
                return "viewpoints";
            }
        } else {
            return null;
        }
    }

    /* Load Settings from URL Params */
    private async loadQueryParams(): Promise<void> {
        const rowsQuery = this.getAxisParam("rows");
        const columnsQuery = this.getAxisParam("columns");
        const dataQuery = this.getAxisParam("data");
        if (rowsQuery) {
            this.rows = rowsQuery;
        }
        if (columnsQuery) {
            this.columns = columnsQuery;
        }
        if (dataQuery) {
            this.data = dataQuery;
        }
        if (
            this.routeQuery.dataId &&
            typeof this.routeQuery.dataId == "string"
        ) {
            this.loadedDataItemId = parseInt(this.routeQuery.dataId);
        }
        if (
            this.routeQuery.rootFactor &&
            typeof this.routeQuery.rootFactor == "string"
        ) {
            this.rootFactorId = parseInt(this.routeQuery.rootFactor);
        }
    }

    /* Load Options for tab */
    private async loadTabOptions(): Promise<void> {
        if (this.tabSettings) {
            this.data = this.indexConversion[this.tabSettings.filter_type];
            this.rows = this.indexConversion[this.tabSettings.row_type];
            this.columns = this.indexConversion[this.tabSettings.column_type];

            await Promise.all([
                this.loadTabUiSettings(
                    this.tabSettings.json != ""
                        ? JSON.parse(this.tabSettings.json)
                        : null
                ),
                this.loadTabDisplayOptions(
                    this.tabSettings.display_flags != ""
                        ? JSON.parse(this.tabSettings.display_flags)
                        : null
                ),
                this.loadTabEditOptions(
                    this.tabSettings.edit_flags != ""
                        ? JSON.parse(this.tabSettings.edit_flags)
                        : null
                ),
            ]);

            this.loadedDataItemId = this.tabSettings.default_filter_id
                ? this.tabSettings.default_filter_id
                : null;
        }
    }

    private async loadTabUiSettings(options: UiSettings) {
        if (options) {
            this.mainColumnWidth = options.mainColumn;
            this.dataColumnWidth = options.dataColumn;
            this.paddingX = options.paddingX;
            this.paddingY = options.paddingY;
            this.borderX = options.borderX;
            this.borderY = options.borderY;
            this.pagination = options.pagination;
            this.rootFactorId = options.rootFactor ? options.rootFactor : -1;
            this.fullSingleCol = options.fullSingleCol
                ? options.fullSingleCol
                : false;
        }
    }

    private async loadTabDisplayOptions(options: {
        score: ScoreOptions;
        classSelector: ScoreClassSettings;
        factor: FactorOptions;
    }) {
        if (options) {
            this.scoreDisplay = { ...options.score };
            this.classSelector = { ...options.classSelector };
            this.factorDisplay = { ...options.factor };
        }
    }

    private async loadTabEditOptions(options: {
        choice: boolean;
        score: boolean;
        customScore: boolean;
        classRating: boolean;
        weights: boolean;
        scoreRule: boolean;
        locks: Locks;
        allChoices: boolean;
        allViewpoints: boolean;
        allFactors: boolean;
        userChoices?: boolean;
        userViewpoints?: boolean;
        selectUserChoices?: boolean;
        selectUserViewpoints?: boolean;
    }) {
        if (options) {
            this.choiceEditable = options.choice;
            this.scoresEditable = options.score;
            this.customScore = options.customScore;
            this.classRating = options.classRating;
            this.weightsEditable = options.weights;
            this.scoreRuleEditable = options.scoreRule;
            this.locks = options.locks;
            this.activeChoices.all = options.allChoices;
            this.activeViewpoints.all = options.allViewpoints;

            this.showAllViewpointsTab = options.allViewpoints;
            this.showAllChoicesTab = options.allChoices;

            this.activeFactors.all = options.allFactors;
            this.userChoicesEnabled =
                options.userChoices != undefined ? options.userChoices : true;
            this.userViewpointsEnabled =
                options.userViewpoints != undefined
                    ? options.userViewpoints
                    : true;
            this.selectUserChoices =
                options.selectUserChoices != undefined
                    ? options.selectUserChoices
                    : true;
            this.selectUserViewpoints =
                options.selectUserViewpoints != undefined
                    ? options.selectUserViewpoints
                    : true;
        } else {
            this.choiceEditable = true;
            this.scoresEditable = true;
            this.customScore = true;
            this.classRating = true;
            this.weightsEditable = true;
            this.scoreRuleEditable = true;
            this.activeChoices.all = true;
            this.activeViewpoints.all = true;
            this.activeFactors.all = true;
            this.userChoicesEnabled = true;
            this.userViewpointsEnabled = true;
            this.selectUserChoices = true;
            this.selectUserViewpoints = true;
            if (!this.embed) {
                // this.onViewpointsChange(this.viewpoints);
                // this.onChoicesChange();
            }
        }
    }

    private async loadDataItem(
        id: number | undefined,
        index: string | undefined
    ): Promise<Choice | Factor | Viewpoint | null> {
        if (id && index) {
            if (index == "choices") {
                return choiceModule.choiceList[id];
            } else if (index == "viewpoints") {
                return viewpointsModule.viewpointList[id];
            } else {
                return await modelModule.fetchFactor(id);
            }
        } else {
            return null;
        }
    }
    /* End of load Options */

    /* Recursivly checks if selected id is a child of 
        the selected root group. Does not get called if 
        root is -1 or if selected factor is the selected root */
    isFactorInRoot(id: number | undefined): boolean {
        if (id && modelModule.factorMap[id]) {
            if (!modelModule.factorMap[id].parent_id) {
                return false;
            } else if (
                id == this.rootFactorId ||
                modelModule.factorMap[id].parent_id == this.rootFactorId
            ) {
                return true;
            } else {
                return this.isFactorInRoot(modelModule.factorMap[id].parent_id);
            }
        } else {
            return false;
        }
    }

    private openSidebar(index: string): void {
        if (this.sidebarIndex != index) {
            this.sidebarIndex = index;
            this.settingsMode = index;
        } else {
            this.sidebarIndex = "sidebar-close";
        }
    }

    private async setChoiceQuery(): Promise<void> {
        if (
            this.querySettings &&
            this.routeQuery.choices &&
            Array.isArray(this.routeQuery.choices)
        ) {
            this.queryChoicesIds = this.routeQuery.choices
                .filter((id) => id != null)
                .map((id) => (id != null ? parseInt(id) : 0));
        } else if (
            this.querySettings &&
            typeof this.routeQuery.choices == "string"
        ) {
            this.queryChoicesIds = [parseInt(this.routeQuery.choices)];
        }
    }

    private async setViewpointQuery(): Promise<void> {
        if (
            this.querySettings &&
            this.routeQuery.viewpoints &&
            Array.isArray(this.routeQuery.viewpoints)
        ) {
            this.queryViewpointsIds = this.routeQuery.viewpoints
                .filter((id) => id != null)
                .map((id) => (id != null ? parseInt(id) : 0));
        } else if (
            this.querySettings &&
            typeof this.routeQuery.viewpoints == "string"
        ) {
            this.queryViewpointsIds = [parseInt(this.routeQuery.viewpoints)];
        }
    }

    private async setFactorQuery(): Promise<void> {
        if (
            this.querySettings &&
            this.routeQuery.factors &&
            Array.isArray(this.routeQuery.factors)
        ) {
            this.queryFactorIds = this.routeQuery.factors
                .filter((id) => id != null)
                .map((id) => (id != null ? parseInt(id) : 0));
        } else if (
            this.querySettings &&
            typeof this.routeQuery.factors == "string"
        ) {
            this.queryFactorIds = [parseInt(this.routeQuery.factors)];
        }
    }

    async mounted(): Promise<void> {
        this.loadingTabSettings = true;
        await this.loadTabOptions();
        this.loadingTabSettings = false;

        if (this.isMainTool) {
            this.selectUserChoices = true;
            this.selectUserViewpoints = true;
        }

        if (
            this.isMainTool &&
            this.routeQuery.settings &&
            this.routeQuery.settings == "true"
        ) {
            this.querySettings = true;
            await Promise.all([
                this.loadQueryParams(),
                this.setChoiceQuery(),
                this.setViewpointQuery(),
                this.setFactorQuery(),
            ]);
            this.$router.replace({ query: {} });
        } else {
            this.querySettings = false;
            await this.loadTabOptions();
        }

        if (this.decisionId) {
            await modelModule.getEnums(this.decisionId);
        }
    }

    @Watch("pageLoading")
    async onPageLoadingChange(): Promise<void> {
        if (!this.pageLoading) {
            if (this.loadedDataItemId && this.axisIndex.data) {
                this.dataItem = await this.loadDataItem(
                    this.loadedDataItemId,
                    this.axisIndex.data
                );
                if (this.data == "factors" && this.dataItem) {
                    this.chosenFactor = [this.dataItem.id];
                }
            }
        }
    }

    @Watch("rootFactorId")
    async onRootFactorIdChange(): Promise<void> {
        if (
            !modelModule.factorTree[this.rootFactorId] ||
            modelModule.factorTree[this.rootFactorId].length == 0
        ) {
            this.loadingTabSettings = true;
            await modelModule.fetchChildFactors(this.rootFactorId);
            this.loadingTabSettings = false;
        }
    }

    @Watch("tabPropId")
    async onTabSettingsChange(): Promise<void> {
        this.loadTabOptions();
    }
}
